/** Share pending startup reads only; completed responses are never cached. */
const startupReadPaths = new Set([
  '/api/auth/me', '/api/user/permissions', '/api/admin/is-admin',
  '/api/auth/role-preview', '/api/flows', '/api/home-chat/sessions',
  '/api/chat-feedback/pending', '/api/ra-contact-chat/conversations',
  '/api/workflow-notifications/unread-count',
]);

interface PendingRead {
  controller: AbortController;
  promise: Promise<Response>;
  consumers: Set<() => void>;
  settled: boolean;
  abandoned?: ReturnType<typeof setTimeout>;
}

const pendingReads = new Map<string, PendingRead>();
const transportIds = new WeakMap<typeof fetch, number>();
let nextTransportId = 0;
let scope = '';
let generation = 0;

const abortError = (invalidated = false) => Object.assign(
  new DOMException('Request aborted.', 'AbortError'),
  { startupReadCancelled: invalidated },
);

export const getStartupReadGeneration = (): number => generation;

export function invalidateStartupReads(): void {
  generation += 1;
  const owners = Array.from(pendingReads.values());
  pendingReads.clear();
  for (const owner of owners) {
    clearTimeout(owner.abandoned);
    for (const cancel of Array.from(owner.consumers)) cancel();
    clearTimeout(owner.abandoned);
    owner.controller.abort();
  }
}

export function setStartupReadScope(nextScope: string): void {
  if (nextScope === scope) return;
  invalidateStartupReads();
  scope = nextScope;
}

export function fetchStartupRead(
  input: RequestInfo | URL,
  init?: RequestInit,
  fetchImpl?: typeof fetch,
): Promise<Response> {
  const transport = fetchImpl ?? fetch;
  // Request objects may carry additional headers/body/options. Leave their
  // original semantics intact instead of constructing an incomplete key.
  if (typeof input !== 'string' && !(input instanceof URL)) return transport(input, init);
  const url = new URL(String(input), window.location.href);
  const method = (init?.method ?? 'GET').toUpperCase();
  if (method !== 'GET' || init?.body != null || !startupReadPaths.has(url.pathname)) {
    return transport(input, init);
  }
  if (init?.signal?.aborted) return Promise.reject(abortError());
  if (!transportIds.has(transport)) transportIds.set(transport, ++nextTransportId);
  const { signal, headers, ...options } = init ?? {};
  const headerEntries: Array<[string, string]> = [];
  new Headers(headers).forEach((value, name) => headerEntries.push([name, value]));
  headerEntries.sort(([left], [right]) => left.localeCompare(right));
  const requestGeneration = generation;
  const key = JSON.stringify([
    scope, generation, url.href, method, headerEntries,
    Object.entries(options).sort(([left], [right]) => left.localeCompare(right)),
    transportIds.get(transport),
  ]);
  let owner = pendingReads.get(key);
  if (!owner) {
    const controller = new AbortController();
    let promise: Promise<Response>;
    try {
      promise = transport(input, { ...init, signal: controller.signal });
    } catch (error) {
      promise = Promise.reject(error);
    }
    owner = { controller, promise, consumers: new Set(), settled: false };
    pendingReads.set(key, owner);
    const created = owner;
    const settle = () => {
      created.settled = true;
      clearTimeout(created.abandoned);
      if (pendingReads.get(key) === created) pendingReads.delete(key);
    };
    void promise.then(settle, settle);
  }
  const joined = owner;
  clearTimeout(joined.abandoned);
  return new Promise<Response>((resolve, reject) => {
    let active = true;
    const detach = () => {
      active = false;
      joined.consumers.delete(invalidate);
      signal?.removeEventListener('abort', cancel);
      if (!joined.settled && joined.consumers.size === 0) {
        // StrictMode cleanup/remount happens before the next event-loop turn.
        // This grace applies only to a pending wire, never a settled response.
        joined.abandoned = setTimeout(() => {
          if (joined.settled || joined.consumers.size !== 0) return;
          if (pendingReads.get(key) === joined) pendingReads.delete(key);
          joined.controller.abort();
        }, 0);
      }
    };
    const stop = (invalidated: boolean) => {
      if (!active) return;
      detach();
      reject(abortError(invalidated));
    };
    const cancel = () => stop(false);
    const invalidate = () => stop(true);
    joined.consumers.add(invalidate);
    signal?.addEventListener('abort', cancel, { once: true });
    if (signal?.aborted) cancel();
    void joined.promise.then((response) => {
      if (!active) return;
      if (requestGeneration !== generation) { invalidate(); return; }
      detach();
      try {
        resolve(response.clone());
      } catch (error) {
        reject(error);
      }
    }, (error) => {
      if (!active) return;
      detach();
      reject(error);
    });
  });
}
